<template>
  <div class="mb-16">
    <div :class="{ 'flex justify-between': isWithNewAtcButtonDesignEnabled }">
      <TitleLabel :product-titles="product.titles" />
      <FavoritesToggle
        v-if="isWithNewAtcButtonDesignEnabled"
        class="!h-48 !border-0"
        :grade-id="selectedOffer?.grade.value"
        :offer-type
        :price="selectedOffer?.price"
        :product-uuid="product.productId"
        :tracking-info="favoritesTrackingInfo"
        zone="details"
      />
    </div>

    <RatingInfo v-if="rating" anchor-id="reviews" :rating />

    <div
      class="mt-24 flex gap-12"
      :class="{
        'flex-col items-start': isWithNewAtcButtonDesignEnabled,
        'items-center': !isWithNewAtcButtonDesignEnabled,
      }"
    >
      <Price
        v-if="product && selectedOffer"
        class="grow"
        :mobile-plan="selectedMobilePlan"
        :price
        :product
        :selected-offer
      />

      <RevButton
        class="min-w-[164px] grow"
        :class="{
          'w-full': isWithNewAtcButtonDesignEnabled,
          'max-w-[256px]': !isWithNewAtcButtonDesignEnabled,
        }"
        data-id="product-page-buy-button-desktop"
        data-qa="product-page-buy-button-desktop"
        variant="primary"
        @click="onAddToCart"
      >
        {{ i18n(translations.addToCart) }}
      </RevButton>

      <FavoritesToggle
        v-if="!isWithNewAtcButtonDesignEnabled"
        class="h-48"
        :grade-id="selectedOffer?.grade.value"
        :offer-type
        :price="selectedOffer?.price"
        :product-uuid="product.productId"
        :tracking-info="favoritesTrackingInfo"
        zone="details"
      />
    </div>
    <TelcoCalculatorAdvertisement
      v-if="(isUS || isGB) && selectedOffer?.price"
      :price="selectedOffer.price"
    >
      <template #icons>
        <USTelcoCalculatorAdvertisementIcons v-if="isUS" />
        <GBTelcoCalculatorAdvertisementIcons v-if="isGB" />
      </template>
    </TelcoCalculatorAdvertisement>
  </div>
</template>

<script lang="ts" setup>
import { computed } from 'vue'

import type { Price as PriceType } from '@backmarket/http-api'
import { MarketCountryCode } from '@backmarket/http-api/src/api-models/MarketCountryCode'
import type { MobilePlanOffer } from '@backmarket/http-api/src/api-specs-b2c-services/mobile-plan/types/mobile-plan-offers'
import type { GetPickersResponse } from '@backmarket/http-api/src/api-specs-navigation-experience/product/pickers'
import type { GetProductResponse } from '@backmarket/http-api/src/api-specs-navigation-experience/product/product'
import type { Rating } from '@backmarket/http-api/src/api-specs-reviews/types/rating'
import { useExperiments } from '@backmarket/nuxt-module-experiments/useExperiments'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useMarketplace } from '@backmarket/nuxt-module-marketplace/useMarketplace'
import { RevButton } from '@ds/components/Button'

import FavoritesToggle from '~/scopes/dashboard/favorites/toggle/FavoritesToggle.vue'
import { fromProductTrackingModel } from '~/scopes/dashboard/favorites/utils/FavoriteTrackingModel'
import { useAddToCart } from '~/scopes/product-core/components/containers/ContainerAddToCart/composables/useAddToCart/useAddToCart'
import Price from '~/scopes/product-core/components/presentationals/Price/Price.vue'
import TelcoCalculatorAdvertisement from '~/scopes/product-core/components/presentationals/TelcoCalculatorAdvertisement/TelcoCalculatorAdvertisement.vue'
import { useUrlParams } from '~/scopes/product-core/composables/useUrlParams/useUrlParams'
import GBTelcoCalculatorAdvertisementIcons from '~/scopes/product-core/pages/ProductDetailPage/gb/components/GBTelcoCalculatorAdvertisementIcons/GBTelcoCalculatorAdvertisementIcons.vue'
import USTelcoCalculatorAdvertisementIcons from '~/scopes/product-core/pages/ProductDetailPage/us/components/USTelcoCalculatorAdvertisementIcons/USTelcoCalculatorAdvertisementIcons.vue'
import RatingInfo from '~/scopes/reviews/reviews-display/components/Rating/Rating.vue'

import { useProductTracking } from '../../composables/useProductTracking'

import translations from './TitleDesktop.translations'
import TitleLabel from './TitleLabel.vue'

const props = withDefaults(
  defineProps<{
    selectedOffer?: GetPickersResponse['selectedOffer']
    product: GetProductResponse
    price: PriceType
    rating?: Rating | null
    tracking: ReturnType<typeof useProductTracking>
    selectedMobilePlan?: MobilePlanOffer
  }>(),
  {
    selectedOffer: null,
    selectedMobilePlan: undefined,
    rating: null,
  },
)

const { offerType } = useUrlParams()
const { addToCart } = useAddToCart()
const i18n = useI18n()
const marketplace = useMarketplace()

const favoritesTrackingInfo = computed(() => {
  return fromProductTrackingModel(props.tracking.product.value)
})

const isUS = computed(() => {
  return marketplace.market.countryCode === MarketCountryCode.US
})
const isGB = computed(() => {
  return marketplace.market.countryCode === MarketCountryCode.GB
})

function onAddToCart() {
  if (props.selectedOffer && props.selectedOffer.offerLegacyId) {
    addToCart({
      listingId: props.selectedOffer.offerLegacyId,
      listingPublicId: props.selectedOffer.offerId,
      tracking: {
        ...props.tracking.product.value,
        list: 'product_page',
        campaign_id: props.selectedOffer.campaignId,
        population_id: props.selectedOffer.populationId,
      },
      partnerPromoCodes: props.product.includedServiceOffers.partnerPromoCodes,
      isTradeInEligible: props.product.isTradeInEligible,
      promoCodes: props.product.promoCodes,
    })
  }
}

const experiments = useExperiments()
const isWithNewAtcButtonDesignEnabled =
  experiments['experiment.ppNewATCButtonDesign'] === 'withNewATCButtonDesign'
</script>
